import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// Copyright 2022 The Parca Authors
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
import { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { usePopper } from 'react-popper';
import { TextWithTooltip, useParcaContext } from '@parca/components';
import { formatDate, timePattern, valueFormatter } from '@parca/utilities';
const virtualElement = {
    getBoundingClientRect: () => {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        return {
            width: 0,
            height: 0,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
        };
    },
};
function generateGetBoundingClientRect(contextElement, x = 0, y = 0) {
    const domRect = contextElement.getBoundingClientRect();
    return () => 
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    ({
        width: 0,
        height: 0,
        top: domRect.y + y,
        left: domRect.x + x,
        right: domRect.x + x,
        bottom: domRect.y + y,
    });
}
const MetricsTooltip = ({ x, y, highlighted, contextElement, sampleUnit, delta, }) => {
    const { timezone } = useParcaContext();
    const [popperElement, setPopperElement] = useState(null);
    const { styles, attributes, ...popperProps } = usePopper(virtualElement, popperElement, {
        placement: 'auto-start',
        strategy: 'absolute',
        modifiers: [
            {
                name: 'preventOverflow',
                options: {
                    tether: false,
                    altAxis: true,
                },
            },
            {
                name: 'offset',
                options: {
                    offset: [30, 30],
                },
            },
        ],
    });
    const update = popperProps.update;
    useEffect(() => {
        if (contextElement != null) {
            virtualElement.getBoundingClientRect = generateGetBoundingClientRect(contextElement, x, y);
            void update?.();
        }
    }, [x, y, contextElement, update]);
    const nameLabel = highlighted?.labels.find(e => e.name === '__name__');
    const highlightedNameLabel = nameLabel !== undefined ? nameLabel : { name: '', value: '' };
    return (_jsx("div", { ref: setPopperElement, style: styles.popper, ...attributes.popper, className: "z-10", children: _jsx("div", { className: "flex max-w-md", children: _jsx("div", { className: "m-auto", children: _jsx("div", { className: "rounded-lg border-gray-300 bg-gray-50 p-3 opacity-90 shadow-lg dark:border-gray-500 dark:bg-gray-900", style: { borderWidth: 1 }, children: _jsx("div", { className: "flex flex-row", children: _jsxs("div", { className: "ml-2 mr-6", children: [_jsx("span", { className: "font-semibold", children: highlightedNameLabel.value }), _jsx("span", { className: "my-2 block text-gray-700 dark:text-gray-300", children: _jsx("table", { className: "table-auto", children: _jsxs("tbody", { children: [delta ? (_jsxs(_Fragment, { children: [_jsxs("tr", { children: [_jsx("td", { className: "w-1/4", children: "Per Second" }), _jsx("td", { className: "w-3/4", children: valueFormatter(highlighted.valuePerSecond, sampleUnit === 'nanoseconds' ? 'CPU Cores' : sampleUnit, 5) })] }), _jsxs("tr", { children: [_jsx("td", { className: "w-1/4", children: "Total" }), _jsx("td", { className: "w-3/4", children: valueFormatter(highlighted.value, sampleUnit, 2) })] })] })) : (_jsxs("tr", { children: [_jsx("td", { className: "w-1/4", children: "Value" }), _jsx("td", { className: "w-3/4", children: valueFormatter(highlighted.valuePerSecond, sampleUnit, 5) })] })), highlighted.duration > 0 && (_jsxs("tr", { children: [_jsx("td", { className: "w-1/4", children: "Duration" }), _jsx("td", { className: "w-3/4", children: valueFormatter(highlighted.duration, 'nanoseconds', 2) })] })), _jsxs("tr", { children: [_jsx("td", { className: "w-1/4", children: "At" }), _jsx("td", { className: "w-3/4", children: formatDate(highlighted.timestamp, timePattern(timezone), timezone) })] })] }) }) }), _jsx("span", { className: "my-2 block text-gray-500", children: highlighted.labels
                                        .filter((label) => label.name !== '__name__')
                                        .map((label) => (_jsx("div", { className: "mr-3 inline-block rounded-lg bg-gray-200 px-2 py-1 text-xs font-bold text-gray-700 dark:bg-gray-700 dark:text-gray-400", children: _jsx(TextWithTooltip, { text: `${label.name}="${label.value}"`, maxTextLength: 37, id: `tooltip-${label.name}-${label.value}` }) }, label.name))) }), _jsxs("div", { className: "flex w-full items-center gap-1 text-xs text-gray-500", children: [_jsx(Icon, { icon: "iconoir:mouse-button-right" }), _jsx("div", { children: "Right click to add labels to query." })] })] }) }) }) }) }) }));
};
export default MetricsTooltip;
